<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        <form @submit.prevent="saveServer">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
                <div class="md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('Edit server') }}</h1>
                    </div>
                    <div class="mt-4 flex md:mt-0 md:ml-4">
                        <button
                            class="btn btn-red shadow-sm rounded-md"
                            type="button"
                            @click="deleteServerModal = true"
                        >
                            {{ $t('Delete server') }}
                        </button>
                    </div>
                </div>
            </div>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div v-show="synced" class="mt-6 p-4 bg-gray-600 shadow">
                    <p class="text-white font-medium">
                        <span class="text-orange-400">{{ $t("WARNING") }}</span>
                        {{ $t("All GdR Servers must be operated by 'SixDesk SAT:GDR', some main input fields are disabled") }}.
                    </p>
                </div>
                <div class="mt-6 shadow sm:rounded-lg">
                    <loading :status="loading"/>
                    <div class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Server details') }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                {{ $t('This information will be displayed publicly') }}.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="name">{{ $t('Name') }}<strong class="text-red-600"> *</strong></label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="name"
                                            v-model="server.name"
                                            :placeholder="$t('Name')"
                                            :class="{ 'text-gray-500 border-gray-300' : synced }"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            required
                                            :disabled="synced"
                                        >
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="name">URL</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="url"
                                            v-model="server.url"
                                            placeholder="URL"
                                            :class="{ 'text-gray-500 border-gray-300' : synced }"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            :disabled="synced"
                                        >
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="md:col-span-3">
                            <div class="py-3">
                                <div class="border-t border-gray-200"></div>
                            </div>
                        </div>
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Server settings') }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                {{ $t('Server settings') }}.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="status">{{ $t('Organizations') }}</label>
                                    <list-view
                                        id="organizations"
                                        type="orgs"
                                        :list="server.orgs"
                                        wrapper="mt-1"
                                        searchable
                                        router
                                    ></list-view>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="status">{{ $t('Status') }}</label>
                                    <div class="mt-1 flex rounded-md">
                                        <input-switch
                                            id="status"
                                            v-model="server.status"
                                            :disabled-label="$t('The server is disabled')"
                                            :enabled-label="$t('The server is activated')"
                                            :readonly="synced"
                                        ></input-switch>
                                    </div>
                                    <div class="mt-2 relative text-xs text-gray-500">
                                        {{ $t('When the server is deactivated, all the organizations within the server will not be able to be operated, inluding centers and users') }}.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-100 text-right px-4 py-3 sm:px-6">
                        <span class="inline-flex">
                            <router-link
                                class="btn btn-secondary shadow-sm rounded-md mr-4"
                                to="/dashboard/admin/servers"
                            >
                                {{ $t('Close') }}
                            </router-link>
                            <button
                                class="btn btn-green shadow-sm rounded-md"
                                type="submit"
                            >
                                {{ $t('Edit server') }}
                            </button>
                        </span>
                    </div>
                </div>
            </div>
        </form>
        <div v-show="deleteServerModal" class="fixed z-20 inset-0 overflow-y-auto">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <transition
                    duration="300"
                    enter-active-class="ease-out duration-300"
                    enter-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="ease-in duration-200"
                    leave-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <div v-show="deleteServerModal" class="fixed inset-0 transition-opacity">
                        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
                    </div>
                </transition>
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen"></span>&#8203;
                <transition
                    enter-active-class="ease-out duration-300"
                    enter-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-active-class="ease-in duration-200"
                    leave-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div
                        v-show="deleteServerModal"
                        aria-labelledby="modal-headline"
                        aria-modal="true"
                        class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
                        role="dialog"
                    >
                        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <div class="sm:flex sm:items-start">
                                <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                    <svg-vue class="h-6 w-6 pb-1 text-red-600" icon="font-awesome.exclamation-triangle-light"></svg-vue>
                                </div>
                                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                    <h3 id="modal-headline" class="text-lg leading-6 font-medium text-gray-900">
                                        {{ $t('Delete server') }}
                                    </h3>
                                    <div class="mt-2">
                                        <p class="text-sm leading-5 text-gray-500">
                                            {{ $t('Are you sure you want to delete the server?') }}
                                            {{ $t('All data will be permanently removed') }}.
                                            {{ $t('This action cannot be undone') }}.
                                        </p>
                                    </div>
                                    <div v-show="!canDelete" class="mt-2">
                                        <p class="text-sm leading-5 text-red-500">
                                            {{ $t('This server has organizations assigned, delete the organizations before deleting the server') }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="bg-gray-100 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                            <button
                                class="btn btn-red mr-2 sm:mr-0"
                                type="button"
                                @click="deleteServer"
                                :disabled="!canDelete"
                            >
                                {{ $t('Delete server') }}
                            </button>
                            <button
                                class="btn btn-white mr-0 sm:mr-2"
                                type="button"
                                @click="deleteServerModal = false"
                            >
                                {{ $t('Cancel') }}
                            </button>
                        </div>
                    </div>
                </transition>
            </div>
        </div>
    </main>
</template>

<script>
export default {
    name: "edit-server",
    metaInfo() {
        return {
            title: this.$i18n.t('Edit server')
        }
    },
    data() {
        return {
            deleteServerModal: false,
            server: {
                id: null,
                name: null,
                url: null,
                orgs: [],
                org_ids: [],
                status: true,
            },
            canDelete: true,
        }
    },
    computed: {
        loading() {
            return this.$store.state.loading.states.main;
        },
        synced() {
            return true;
        },
    },
    beforeMount() {
        this.$store.commit('resetLoading');
    },
    mounted() {
        this.getServer();
    },
    methods: {
        async saveServer() {
            const self = this;
            self.$store.commit('loadSections');
            axios.put('api/dashboard/admin/servers/' + self.$route.params.id, self.server).then(function () {
                self.$store.commit('unloadSections');
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data updated correctly').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.$store.commit('unloadSections');
            });
        },
        getServer() {
            const self = this;
            self.$store.commit('loadSections');
            axios.get('api/dashboard/admin/servers/' + self.$route.params.id).then(function (response) {
                self.server = response.data;
                self.canDelete = !response.data.orgs.length > 0;
                self.$store.commit('unloadSections');
            }).catch(function () {
                self.$store.commit('unloadSections');
            });
        },
        deleteServer() {
            const self = this;
            axios.delete('api/dashboard/admin/servers/' + self.$route.params.id).then(function () {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data deleted successfully').toString(),
                    type: 'success'
                });
                self.$router.push('/dashboard/admin/servers');
            }).catch(function () {
                self.deleteServerModal = false;
            });
        }
    }
}
</script>
